import React from 'react';
import { X } from 'lucide-react';

export type BannerVariant = 'info' | 'warning' | 'error' | 'success';

const VARIANT_VAR: Record<BannerVariant, string> = {
  info: 'var(--info)',
  warning: 'var(--warning)',
  error: 'var(--danger)',
  success: 'var(--success)',
};

export interface BannerProps {
  variant?: BannerVariant;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
  style?: React.CSSProperties;
}

/** Inline alert/banner. Tinted from the semantic token via color-mix (theme-aware). */
export function Banner({ variant = 'info', icon, title, children, action, onDismiss, style }: BannerProps) {
  const color = VARIANT_VAR[variant];
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 'var(--radius-lg)',
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      {icon && <span style={{ color, flex: 'none', display: 'flex' }}>{icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</div>}
        {children && (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: title ? 2 : 0 }}>{children}</div>
        )}
      </div>
      {action}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{ flex: 'none', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

export default Banner;
